// Original items written for this site, inspired by the public four-category
// adult attachment model (secure, anxious-preoccupied, dismissive-avoidant,
// fearful-avoidant). An informal self-reflection quiz, not a clinical
// assessment or diagnostic instrument.

export type AttachmentStyle = 'secure' | 'anxious' | 'avoidant' | 'fearful';

export interface AttachmentQuestion {
  id: string;
  text: string;
  style: AttachmentStyle;
}

export const ATTACHMENT_STYLES: AttachmentStyle[] = ['secure', 'anxious', 'avoidant', 'fearful'];

export const ATTACHMENT_QUESTIONS: AttachmentQuestion[] = [
  // Secure
  {
    id: 'sec1',
    text: "I find it easy to get close to others and don't worry much about being abandoned.",
    style: 'secure',
  },
  {
    id: 'sec2',
    text: "I'm comfortable depending on people close to me, and having them depend on me.",
    style: 'secure',
  },
  {
    id: 'sec3',
    text: "Conflict with someone I love doesn't make me question whether they still care.",
    style: 'secure',
  },
  {
    id: 'sec4',
    text: "I can ask for support without feeling like I'm being a burden.",
    style: 'secure',
  },
  {
    id: 'sec5',
    text: 'I trust that people who love me will stay, even after an argument.',
    style: 'secure',
  },
  // Anxious (preoccupied)
  {
    id: 'anx1',
    text: "I worry that people I care about don't love me as much as I love them.",
    style: 'anxious',
  },
  {
    id: 'anx2',
    text: 'I need frequent reassurance that a relationship is okay.',
    style: 'anxious',
  },
  {
    id: 'anx3',
    text: "When someone I'm close to is quiet or distant, I assume something is wrong between us.",
    style: 'anxious',
  },
  {
    id: 'anx4',
    text: 'I check my phone anxiously for a reply from someone I care about.',
    style: 'anxious',
  },
  {
    id: 'anx5',
    text: "I've stayed in a relationship mostly out of fear of being alone.",
    style: 'anxious',
  },
  // Avoidant (dismissive)
  {
    id: 'avo1',
    text: 'I feel uncomfortable when a relationship starts to feel too close.',
    style: 'avoidant',
  },
  {
    id: 'avo2',
    text: "I'd rather handle my problems alone than lean on someone else.",
    style: 'avoidant',
  },
  {
    id: 'avo3',
    text: 'I keep some emotional distance even from people I care about.',
    style: 'avoidant',
  },
  {
    id: 'avo4',
    text: 'Being fully open about my feelings makes me feel exposed.',
    style: 'avoidant',
  },
  { id: 'avo5', text: 'I pull back when someone starts to depend on me.', style: 'avoidant' },
  // Fearful (disorganized)
  {
    id: 'fea1',
    text: 'I want closeness, but getting close to someone also makes me want to pull away.',
    style: 'fearful',
  },
  {
    id: 'fea2',
    text: "I've pushed people away right when things were starting to feel good.",
    style: 'fearful',
  },
  { id: 'fea3', text: "I don't fully trust people even when I want to.", style: 'fearful' },
  {
    id: 'fea4',
    text: 'My feelings about being close to someone can flip quickly, from wanting it to fearing it.',
    style: 'fearful',
  },
  {
    id: 'fea5',
    text: 'Past experiences make it hard for me to believe closeness stays safe.',
    style: 'fearful',
  },
];

export const ATTACHMENT_BLURBS: Record<AttachmentStyle, { name: string; blurb: string }> = {
  secure: {
    name: 'Secure attachment',
    blurb:
      "You're generally comfortable with both closeness and independence, and a disagreement doesn't make you doubt the relationship itself.",
  },
  anxious: {
    name: 'Anxious (preoccupied) attachment',
    blurb:
      "You crave closeness and reassurance, and distance from someone you love can feel like a warning sign even when it isn't one.",
  },
  avoidant: {
    name: 'Avoidant (dismissive) attachment',
    blurb:
      'You value independence and space, and too much closeness, too fast, can feel more like pressure than comfort.',
  },
  fearful: {
    name: 'Fearful (disorganized) attachment',
    blurb:
      'You want closeness and feel wary of it at the same time, so relationships can feel like pulling someone in and pushing them away in turns.',
  },
};
